import React, { useEffect, useState, useContext } from "react";
import apisettings from "../apiSettings";
import awsconfig from '../awsSettings';
import i18n from '../utilities/i18n';

export const ConfigContext = React.createContext();

const TIMEOUT_MS = 8000;
const MAX_DELAY_MS = 15000;

// A 404 means the resource doesn't exist at that URL - retrying can't fix
// that, so it's treated as non-retryable instead of being retried forever.
class NotFoundError extends Error {}

// Fetches JSON with a per-attempt timeout (fetch has none by default) and
// exponential backoff retry, so a slow/dead connection can't hang forever
// or silently give up after a single failed attempt.
async function fetchJsonWithRetry(url, { maxRetries = Infinity, onRetry } = {}) {
  let attempt = 0;
  while (true) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (response.status === 404) {
        throw new NotFoundError(`Request to ${url} failed with status 404 (not found)`);
      }
      if (!response.ok) {
        throw new Error(`Request to ${url} failed with status ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (error instanceof NotFoundError) {
        throw error;
      }
      attempt++;
      if (attempt > maxRetries) {
        throw error;
      }
      console.log(`Request to ${url} failed (attempt ${attempt}): ${error.message}. Retrying in ${MAX_DELAY_MS}ms.`);
      if (onRetry) onRetry(attempt, MAX_DELAY_MS);
      await new Promise((resolve) => setTimeout(resolve, MAX_DELAY_MS));
    } finally {
      clearTimeout(timer);
    }
  }
}

export const ConfigProvider = ({ children }) => {
  const [KioskConfig, setKioskConfig] = useState();
  const [CustomerConfig, setCustomerConfig] = useState();
  const [EnabledFeatures, setEnabledFeatures] = useState();
  const [StyleConfig, setStyleConfig] = useState();
  const [RetryStatus, setRetryStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchCustomerStyle(customer) {
      try {
        const data = await fetchJsonWithRetry(
          `https://${awsconfig.Storage.AWSS3.bucket}.s3-${awsconfig.Storage.AWSS3.region}.amazonaws.com/public/${customer}/styles/kiosk/customStyles.json`,
          { maxRetries: 0 }
        );
        console.log("customStyles.json loaded!", data);
        return data;
      } catch (error) {
        console.log("customStyles.json not loaded, still happy.");
        return {};
      }
    }

    async function fetchKioskConfig() {
      return fetchJsonWithRetry(apisettings.BASE_URL + "/GetConfig", {
        onRetry: (attempt, delay) => {
          if (!cancelled) setRetryStatus(`Waiting for kiosk config (attempt ${attempt})...`);
        },
      });
    }

    async function fetchCustomerConfig() {
      return fetchJsonWithRetry(apisettings.BASE_URL + "/WhoAmI", {
        onRetry: (attempt, delay) => {
          if (!cancelled) setRetryStatus(`Waiting for customer config (attempt ${attempt})...`);
        },
      });
    }

    async function fetchTranslations(language, customer) {
      try {
        const data = await fetchJsonWithRetry(
          `https://${awsconfig.Storage.AWSS3.bucket}.s3-${awsconfig.Storage.AWSS3.region}.amazonaws.com/public/${customer}/text/kiosk/texts-${language}.json`,
          { maxRetries: 0 }
        );
        i18n.addResourceBundle(language, 'combined', data);
        console.log(`texts-${language}.json loaded!`);
      } catch (error) {
        console.log(`error loading texts-${language}.json! ` + error);
      }

      try {
        const data = await fetchJsonWithRetry(`/global-${language}.json`, { maxRetries: 3 });
        i18n.addResourceBundle(language, 'combined', data);
        console.log(`global-${language}.json loaded!`);
      } catch (error) {
        console.log(`error loading /global-${language}.json! ` + error);
      }
    }

    async function fetchAllConfigsAndTranslations() {
      let kioskConfig = await fetchKioskConfig();
      let customerConfig = await fetchCustomerConfig();
      let styleConfig = await fetchCustomerStyle(customerConfig.Customer);
      if (customerConfig.EnabledFeatures && customerConfig.EnabledFeatures.includes("multilingual")) {
        if (customerConfig.SupportedLanguages && customerConfig.SupportedLanguages.length > 0) {
          for (const lang of customerConfig.SupportedLanguages) {
            await fetchTranslations(lang, customerConfig.Customer);
          }
        }
      }
      else {
        await fetchTranslations('nl', customerConfig.Customer);
      }
      console.log("All configs and translations loaded!");
      if (cancelled) return;
      setKioskConfig(kioskConfig);
      setStyleConfig(styleConfig);
      setEnabledFeatures(customerConfig.EnabledFeatures);
      setCustomerConfig(customerConfig);
      setRetryStatus(null);
      console.log("All configs and translations set!");
    }

    // Defense in depth: fetchKioskConfig/fetchCustomerConfig already retry
    // indefinitely on their own, so this shouldn't normally throw. If some
    // other unexpected error slips through anyway, restart the whole
    // sequence instead of leaving the UI stuck on a crashed load.
    async function runWithRestart() {
      while (!cancelled) {
        try {
          await fetchAllConfigsAndTranslations();
          return;
        } catch (error) {
          console.log("Unexpected error loading configs, restarting load in 5s.", error);
          setRetryStatus(`${error.message}. Retrying...`);
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      }
    }

    runWithRestart();

    return () => {
      cancelled = true;
    };
  }, []);

  if (StyleConfig && KioskConfig && CustomerConfig && EnabledFeatures) {
    return (
      <ConfigContext.Provider
        value={{ KioskConfig, CustomerConfig, StyleConfig, EnabledFeatures }}
      >
        {children}
      </ConfigContext.Provider>
    );
  }
  return RetryStatus ? `Loading.... ${RetryStatus}` : "Loading....";
};

export const useConfig = () => useContext(ConfigContext);
